import { useT } from "@/context/LanguageContext";
import { translations } from "@/data/translations";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

export default function Pricing() {
  const t = useT(translations.pricing);

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-20 md:py-32"
    >
      <div className="absolute inset-0 bg-[#252b36]" />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-medium tracking-[0.3em] uppercase text-[#b87b56]">
            {t.caption}
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mt-3"
            style={{ fontFamily: "Oswald, sans-serif" }}
          >
            {t.title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Free tier */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            data-testid="pricing-free"
            className="bg-[#39424F] border border-white/10 p-8 flex flex-col relative"
          >
            <h3
              className="text-2xl font-bold text-white uppercase tracking-wider mb-1"
              style={{ fontFamily: "Oswald, sans-serif" }}
            >
              {t.free.name}
            </h3>
            <div className="flex items-baseline gap-1 mb-1">
              <span
                className="text-4xl font-bold text-white"
                style={{ fontFamily: "Oswald, sans-serif" }}
              >
                {t.free.price}
              </span>
            </div>
            <span className="text-xs text-slate-400 uppercase tracking-wider mb-6">
              {t.free.period}
            </span>

            <ul className="space-y-3 flex-1 mb-8">
              {t.free.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <Check size={16} className="text-[#b87b56] mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-300">{f}</span>
                </li>
              ))}
            </ul>

            <a
              href="https://apps.apple.com/us/app/boxing-mma-timer/id6760870249"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pricing-free-cta"
              className="h-12 flex items-center justify-center rounded-sm border border-white/10 bg-transparent hover:bg-white/5 text-white font-bold uppercase tracking-wider text-sm transition-all"
            >
              {t.free.cta}
            </a>
          </motion.div>

          {/* Premium tier */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
            data-testid="pricing-premium"
            className="bg-[#39424F] border border-[#b87b56]/40 p-8 flex flex-col relative"
          >
            {/* Premium badge */}
            <div className="absolute top-0 right-0 bg-[#b87b56] px-3 py-1">
              <span className="text-[10px] font-bold text-white uppercase tracking-wider">
                PRO
              </span>
            </div>

            <h3
              className="text-2xl font-bold text-[#b87b56] uppercase tracking-wider mb-1"
              style={{ fontFamily: "Oswald, sans-serif" }}
            >
              {t.premium.name}
            </h3>
            <div className="flex items-baseline gap-1 mb-1">
              <span
                className="text-4xl font-bold text-white"
                style={{ fontFamily: "Oswald, sans-serif" }}
              >
                {t.premium.price}
              </span>
            </div>
            <span className="text-xs text-slate-400 uppercase tracking-wider mb-6">
              {t.premium.period}
            </span>

            <ul className="space-y-3 flex-1 mb-8">
              {t.premium.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <Check size={16} className="text-[#b87b56] mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-300">{f}</span>
                </li>
              ))}
            </ul>

            <a
              href="https://apps.apple.com/us/app/boxing-mma-timer/id6760870249"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pricing-premium-cta"
              className="h-12 flex items-center justify-center rounded-sm bg-[#b87b56] hover:bg-[#a66d4b] text-white font-bold uppercase tracking-wider text-sm transition-all hover:translate-y-[-1px] hover:shadow-[0_4px_20px_rgba(184,123,86,0.4)]"
            >
              {t.premium.cta}
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
